import { DatePipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";

import { NotificationService } from "src/app/core/notification.service";
import { StrikeService } from "src/app/core/strike.service";
import { ApiResponse } from "src/app/shared/interfaces/api-response";
import { UpdatedStrike } from "src/app/shared/interfaces/updated-strike";

@Component({
  selector: "app-strike-edit",
  templateUrl: "./strike-edit.component.html",
  styleUrls: ["./strike-edit.component.css"],
})
export class StrikeEditComponent implements OnInit {
  disableButton = false;
  paramID = "";

  strikeEditForm = new FormGroup({
    title: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    startDate: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    endDate: new FormControl("", { nonNullable: true }),
    minThresholdHours: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    minThresholdDays: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    participationBenefitAmt: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    helperBenefitAmt: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    lastWeekMinThresholdHours: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    lastWeekMinThresholdDays: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    lastWeekParticipationBenefitAmt: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    lastWeekHelperBenefitAmt: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
    comments: new FormControl("", {
      validators: Validators.required,
      nonNullable: true,
    }),
  });

  // these getters are needed for the form validation in the html https://angular.io/guide/form-validation#:~:text=as%20an%20array.-,This,-example%20also%20adds
  get title() {
    return this.strikeEditForm.get("title");
  }
  get startDate() {
    return this.strikeEditForm.get("startDate");
  }
  get minThresholdHours() {
    return this.strikeEditForm.get("minThresholdHours");
  }
  get minThresholdDays() {
    return this.strikeEditForm.get("minThresholdDays");
  }
  get participationBenefitAmt() {
    return this.strikeEditForm.get("participationBenefitAmt");
  }
  get helperBenefitAmt() {
    return this.strikeEditForm.get("helperBenefitAmt");
  }
  get lastWeekMinThresholdHours() {
    return this.strikeEditForm.get("lastWeekMinThresholdHours");
  }
  get lastWeekMinThresholdDays() {
    return this.strikeEditForm.get("lastWeekMinThresholdDays");
  }
  get lastWeekParticipationBenefitAmt() {
    return this.strikeEditForm.get("lastWeekParticipationBenefitAmt");
  }
  get lastWeekHelperBenefitAmt() {
    return this.strikeEditForm.get("lastWeekHelperBenefitAmt");
  }
  get comments() {
    return this.strikeEditForm.get("comments");
  }

  constructor(
    private datepipe: DatePipe,
    private strikes: StrikeService,
    private notification: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    this.paramID = this.route.snapshot.paramMap.get("id") || "";
    if (this.paramID) {
      this.loadStrikeInfo();
    }
    else {
      this.notification.displayError("Missing required 'id' parameter");
    }
  }

  async loadStrikeInfo() {
    const response = await this.strikes.getStrikeDetails(this.paramID);

    this.strikeEditForm.controls["title"].setValue(response.Title);
    this.strikeEditForm.controls["startDate"].setValue(response.StartDate);
    this.strikeEditForm.controls["endDate"].setValue(response.EndDate);
    this.strikeEditForm.controls["minThresholdHours"].setValue(
      response.MinThresholdHours,
    );
    this.strikeEditForm.controls["minThresholdDays"].setValue(
      response.MinThresholdDays,
    );
    this.strikeEditForm.controls["participationBenefitAmt"].setValue(
      response.ParticipationBenefitAmt,
    );
    this.strikeEditForm.controls["helperBenefitAmt"].setValue(
      response.HelperBenefitAmt,
    );
    this.strikeEditForm.controls["lastWeekMinThresholdHours"].setValue(
      response.LastWeekMinThresholdHours,
    );
    this.strikeEditForm.controls["lastWeekMinThresholdDays"].setValue(
      response.LastWeekMinThresholdDays,
    );
    this.strikeEditForm.controls["lastWeekParticipationBenefitAmt"].setValue(
      response.LastWeekParticipationBenefitAmt,
    );
    this.strikeEditForm.controls["lastWeekHelperBenefitAmt"].setValue(
      response.LastWeekHelperBenefitAmt,
    );
    this.strikeEditForm.controls["comments"].setValue(response.Comments);
  }

  async submitStrike() {
    this.disableButton = true;

    const formattedStartDate: string | null = this.datepipe.transform(
      this.strikeEditForm.value.startDate,
      "yyyy-MM-dd",
    );

    const formattedEndDate: string | null = this.datepipe.transform(
      this.strikeEditForm.value.endDate,
      "yyyy-MM-dd",
    );

    if (typeof formattedStartDate != "string") {
      this.notification.displayError("Please fill out all fields");
      return;
    }

    const updatedStrike: UpdatedStrike = {
      Title: this.strikeEditForm.getRawValue().title,
      StartDate: formattedStartDate,
      EndDate: formattedEndDate,
      ID: this.paramID,
      MinThresholdHours: this.strikeEditForm.getRawValue().minThresholdHours,
      MinThresholdDays: this.strikeEditForm.getRawValue().minThresholdDays,
      ParticipationBenefitAmt:
        this.strikeEditForm.getRawValue().participationBenefitAmt,
      HelperBenefitAmt: this.strikeEditForm.getRawValue().helperBenefitAmt,
      LastWeekMinThresholdHours:
        this.strikeEditForm.getRawValue().lastWeekMinThresholdHours,
      LastWeekMinThresholdDays:
        this.strikeEditForm.getRawValue().lastWeekMinThresholdDays,
      LastWeekParticipationBenefitAmt:
        this.strikeEditForm.getRawValue().lastWeekParticipationBenefitAmt,
      LastWeekHelperBenefitAmt:
        this.strikeEditForm.getRawValue().lastWeekHelperBenefitAmt,
      Comments: this.strikeEditForm.getRawValue().comments,
    };

    const response: ApiResponse =
      await this.strikes.updateStrike(updatedStrike);

    if (response.success) {
      this.router.navigateByUrl("/home");
      this.notification.displaySuccess("Strike successfully updated");
    } else {
      this.notification.displayError(
        "Please make sure all fields are filled out and try again.",
      );
    }

    this.disableButton = false;
  }
}
